import React, { useState, useRef, useEffect } from "react";
import styles from "./chat.module.scss";
const { baseURL } = require("../../config");
export default function Chat() {
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);

  const controllerRef = useRef(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, loading]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = {
      role: "user",
      content: input
    };

    setMessages(prev => [...prev, userMessage]);
    setInput("");
    setLoading(true);

    const controller = new AbortController();
    controllerRef.current = controller;

    try {
      const token = localStorage.getItem("idToken");

      const res = await fetch(`${baseURL}/chatBot/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          // Accept: "text/event-stream",
          Authorization: token ? `Bearer ${token}` : undefined
        },
        body: JSON.stringify({
          message: userMessage.content,
          conversationHistory: messages.map(m => ({
            role: m.role,
            content: [{ type: "text", text: m.content }]
          }))
        }),
        signal: controller.signal
      });
      if (res.status === 429) {
        setMessages(prev => [
          ...prev,
          {
            role: "assistant",
            content:
              "⚠️ You're sending messages too fast. Please try again later."
          }
        ]);

        setLoading(false);
        return;
      }
      if (res.status === 500) {
        const data = await res.json();

        setMessages(prev => [
          ...prev,
          {
            role: "assistant",
            content: `⚠️ ${data.error}`
          }
        ]);

        setLoading(false);
        return;
      }

      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }
      const contentType = res.headers.get("content-type");

      // 🔥 STREAMING MODE
      if (contentType && contentType.includes("text/event-stream")) {
        const reader = res.body.getReader();
        const decoder = new TextDecoder("utf-8");

        let buffer = "";
        let assistantMessage = "";

        setMessages(prev => [...prev, { role: "assistant", content: "" }]);

        let done = false;

        while (!done) {
          const result = await reader.read();
          done = result.done;

          if (done) break;

          buffer += decoder.decode(result.value, { stream: true });

          const parts = buffer.split("\n\n");
          buffer = parts.pop() || "";

          for (const part of parts) {
            if (!part.startsWith("data: ")) continue;

            let json;

            try {
              json = JSON.parse(part.replace("data: ", ""));
            } catch (err) {
              console.error("Invalid JSON chunk:", err);
              continue;
            }

            if (json.type === "text") {
              assistantMessage += json.content;

              setMessages(prev => {
                const updated = [...prev];
                updated[updated.length - 1] = {
                  role: "assistant",
                  content: assistantMessage
                };
                return updated;
              });
            }

            if (json.type === "done") {
              setLoading(false);
            }
          }
        }
      }

      // 🔥 NORMAL JSON MODE
      else {
        const data = await res.json();

        setMessages(prev => [
          ...prev,
          {
            role: "assistant",
            content: data.response
          }
        ]);

        setLoading(false);
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        console.error("Chat error:", err);
      }
    } finally {
      setLoading(false);
    }
  };

  const stopStreaming = () => {
    if (controllerRef.current) {
      controllerRef.current.abort();
    }
    setLoading(false);
  };

  return (
    <div className={`${styles.container} ${styles.embedded}`}>
      <div className={styles.chatWindow}>
        {messages.map((msg, index) => (
          <div key={index} className={`${styles.message} ${styles[msg.role]}`}>
            {msg.content}
          </div>
        ))}

        {loading && (
          <div className={styles.typingContainer}>
            <div className={styles.typingBubble}>
              <span className={styles.dot} />
              <span className={styles.dot} />
              <span className={styles.dot} />
            </div>
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      <div className={styles.inputContainer}>
        <textarea
          value={input}
          onChange={e => setInput(e.target.value)}
          rows={2}
          className={styles.textarea}
          placeholder="Type a message..."
          onKeyDown={e => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              sendMessage();
            }
          }}
        />

        <div className={styles.ButtonEmbedded}>
          <button
            onClick={sendMessage}
            disabled={loading}
            className={styles.button}
          >
            Send
          </button>

          {loading && (
            <button
              onClick={stopStreaming}
              className={`${styles.button} ${styles.stopButton}`}
            >
              Stop
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
